import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface CaptchaSettingValue {
  enabled: boolean;
}

interface AppSettingRow {
  key: string;
  value: { enabled: boolean };
  updated_at: string | null;
  updated_by: string | null;
}

export function useCaptchaEnabled() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["app-settings", "captcha_enabled"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("app_settings")
        .select("*")
        .eq("key", "captcha_enabled")
        .single();

      if (error) {
        console.error("Failed to fetch CAPTCHA setting:", error);
        return null;
      }

      return data as AppSettingRow;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  // Extract enabled value from the row, default to false during loading to prevent login issues
  // IMPORTANT: This setting only controls the frontend CAPTCHA widget.
  // If CAPTCHA is enabled in Supabase Dashboard (Auth → CAPTCHA), it must also be enabled here.
  const settingValue = data?.value as unknown as CaptchaSettingValue | undefined;
  
  // Default to false during loading to prevent blocking login
  // Once loaded, use the actual setting value (defaults to false if not found)
  const isEnabled = isLoading ? false : (settingValue?.enabled ?? false);

  return {
    isCaptchaEnabled: isEnabled,
    isLoading,
    error,
  };
}
